import React, { useCallback, useMemo } from "react";
import { CFormLabel } from "@coreui/react";
import { CDatePicker, CTimePicker } from "@tradingdroid/coreui-pro";
import dayjs from "dayjs";

interface Props {
  label?: string;
  value: string; // ISO format: "2024-01-01T00:00"
  onChange: (value: string) => void;
  disabled?: boolean;
  minDate?: string; // ISO format: "2024-01-01T00:00"
  maxDate?: string; // ISO format: "2024-01-01T00:00"
}

export const CDateTimePicker: React.FC<Props> = ({
  label,
  value,
  onChange,
  disabled = false,
  minDate,
  maxDate,
}) => {
  // Parse the ISO datetime string into date and time parts
  const { dateValue, timeValue, minDateValue, maxDateValue } = useMemo(() => {
    if (!value) {
      return {
        dateValue: undefined,
        timeValue: undefined,
        minDateValue: undefined,
        maxDateValue: undefined,
      };
    }

    // CDatePicker expects a Date object or ISO string
    const dateValue = dayjs(value).toDate();

    // Minute precision (no seconds).
    const timeValue = `${dayjs(value).format("HH:mm")}:00`;

    // Convert minDate and maxDate to Date objects
    const minDateValue = minDate ? dayjs(minDate).toDate() : undefined;
    const maxDateValue = maxDate ? dayjs(maxDate).toDate() : undefined;

    return { dateValue, timeValue, minDateValue, maxDateValue };
  }, [value, minDate, maxDate]);

  // Handle date picker change
  const handleDateChange = useCallback(
    (newDate: Date | null) => {
      if (!newDate) return;

      const currentTime = value ? dayjs(value).format("HH:mm") : "00:00";
      const newDateTime = `${dayjs(newDate).format("YYYY-MM-DD")}T${currentTime}`;

      onChange(newDateTime);
    },
    [value, onChange],
  );

  // Handle time picker change
  const handleTimeChange = useCallback(
    (newTime: string | null) => {
      if (!newTime) return;

      // Take HH:mm, drop seconds.
      const timePart = newTime.substring(0, 5);
      const currentDate = value ? dayjs(value).format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD");

      onChange(`${currentDate}T${timePart}`);
    },
    [value, onChange],
  );

  return (
    <div className="date-time-picker-wrapper">
      {label && <CFormLabel>{label}</CFormLabel>}
      <div className="d-flex date-time-picker-row">
        <div className="date-time-picker-date">
          <CDatePicker
            locale="en-GB"
            date={dateValue}
            onDateChange={handleDateChange}
            disabled={disabled}
            selectionType="day"
            minDate={minDateValue}
            maxDate={maxDateValue}
            footer={false}
            timepicker={false}
            closeOnSelect={true}
            inputReadOnly={true}
          />
        </div>
        <div className="date-time-picker-time">
          <CTimePicker
            locale="en-GB"
            time={timeValue}
            onTimeChange={handleTimeChange}
            disabled={disabled}
            seconds={false}
          />
        </div>
      </div>
    </div>
  );
};
